import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FiCheck, FiX } from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import { showNotification } from './Notification';
import './AttendanceLink.css';

const AttendanceLink = () => {
  const { token } = useParams();
  const { theme } = useContext(ThemeContext);
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [activeSession, setActiveSession] = useState('single');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLinkData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sortTrainees = (trainees) => {
    return [...trainees].sort((a, b) => {
      const aValue = a.serial_number && /^\d+$/.test(a.serial_number) ? parseInt(a.serial_number, 10) : null;
      const bValue = b.serial_number && /^\d+$/.test(b.serial_number) ? parseInt(b.serial_number, 10) : null;

      if (aValue !== null && bValue !== null) {
        return aValue - bValue;
      }

      return (a.tag_number || '').localeCompare(b.tag_number || '');
    });
  };

  const fetchLinkData = async () => {
    try {
      const response = await axios.get(`/api/attendance/link/${token}`);
      const traineesArray = Array.isArray(response.data?.trainees) ? response.data.trainees : [];
      const processed = {
        ...response.data,
        trainees: sortTrainees(
          traineesArray.map(t => ({
            ...t,
            attendance_sessions: t.attendance_sessions || {}
          }))
        )
      };
      setLinkData(processed);
      setActiveSession(processed.link?.has_sessions ? 'morning' : 'single');
    } catch (error) {
      showNotification('Invalid or expired attendance link', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendance = async (traineeId, status) => {
    const currentSession = linkData?.link?.has_sessions ? activeSession : 'single';
    setSubmitting({ ...submitting, [traineeId]: true });
    try {
      await axios.post('/api/attendance/submit', {
        token,
        trainee_id: traineeId,
        status,
        session: currentSession
      });
      // Update local state
      setLinkData(prev => ({
        ...prev,
        trainees: Array.isArray(prev?.trainees) ? prev.trainees.map(t =>
          t.id === traineeId 
            ? { 
                ...t, 
                attendance_status: currentSession === 'single' ? status : (t.attendance_status || null),
                attendance_sessions: {
                  ...(t.attendance_sessions || {}),
                  [currentSession]: status
                }
              } 
            : t
        ) : []
      }));
    } catch (error) {
      showNotification('Error recording attendance', 'error');
    } finally {
      setSubmitting({ ...submitting, [traineeId]: false });
    }
  };

  const getTraineeStatus = (trainee) => {
    if (!linkData?.link?.has_sessions) {
      return trainee.attendance_status || null;
    }
    return (trainee.attendance_sessions && trainee.attendance_sessions[activeSession]) || null;
  };

  const formatSessionLabel = (session) => {
    if (session === 'single') return 'Single Session';
    return `${session.charAt(0).toUpperCase()}${session.slice(1)} Session`;
  };

  if (loading) {
    return <div className="attendance-link-loading">Loading...</div>;
  }

  if (!linkData) {
    return <div className="attendance-link-error">Invalid attendance link</div>;
  }

  const filteredTrainees = linkData && Array.isArray(linkData.trainees)
    ? linkData.trainees.filter(trainee => {
        if (!searchTerm.trim()) return true;
        const needle = searchTerm.toLowerCase();
        const haystack = `${trainee.tag_number || ''} ${trainee.first_name || ''} ${trainee.last_name || ''}`.toLowerCase();
        return haystack.includes(needle);
      })
    : [];

  return (
    <div className="attendance-link" data-theme={theme}>
      <div className="attendance-link-header">
        <img
          src="/logo/css-farms-tvet-logo.png"
          alt="CSS FARMS IDEAS-TVET Logo"
          className="attendance-link-logo"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <h1>Attendance Recording</h1>
        <div className="link-info">
          <h2>{linkData?.link?.course_title || 'Course'}</h2>
          <p>Date: {linkData?.link?.attendance_date ? new Date(linkData.link.attendance_date).toLocaleDateString() : 'N/A'}</p>
          <span className="link-session-indicator">
            {formatSessionLabel(linkData?.link?.has_sessions ? activeSession : 'single')}
          </span>
        </div>
      </div>

      {linkData?.link?.has_sessions && (
        <div className="session-toggle">
          {['morning', 'afternoon'].map(session => (
            <button
              key={session}
              className={`session-btn ${activeSession === session ? 'active' : ''}`}
              onClick={() => setActiveSession(session)}
            >
              {session === 'morning' ? 'Morning Session' : 'Afternoon Session'}
            </button>
          ))}
        </div>
      )}

      <div className="attendance-search">
        <input
          type="text"
          placeholder="Search by Tag Number (SN) or Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="trainees-list">
        {filteredTrainees.length === 0 ? (
          <div className="no-results">
            {searchTerm.trim()
              ? `No trainee found for "${searchTerm}"`
              : 'No trainees available yet.'}
          </div>
        ) : (
        filteredTrainees.map((trainee, index) => {
          const currentStatus = getTraineeStatus(trainee);
          return (
          <div key={trainee.id} className="trainee-item">
            <div className="trainee-info">
              <span className="serial-count">{index + 1}.</span>
              <span className="tag-number">{trainee.tag_number}</span>
              <span className="trainee-name">{trainee.first_name} {trainee.last_name}</span>
            </div>
            <div className="attendance-buttons">
              <button
                className={`btn-attendance present ${currentStatus === 'present' ? 'active' : ''}`}
                onClick={() => handleAttendance(trainee.id, 'present')}
                disabled={submitting[trainee.id]}
              >
                <FiCheck /> Present
              </button>
              <button
                className={`btn-attendance absent ${currentStatus === 'absent' ? 'active' : ''}`}
                onClick={() => handleAttendance(trainee.id, 'absent')}
                disabled={submitting[trainee.id]}
              >
                <FiX /> Absent
              </button>
            </div>
          </div>
        )}))}
      </div>
    </div>
  );
};

export default AttendanceLink;


